/**
 * Module: components/copilot/CopilotTrigger
 *
 * Floating-action bubble that opens the ChatWidget from any page other than
 * /home. Designed to be the one reliable entry point to the cross-page widget:
 * the left-nav "Copilot" item navigates to /home (full surface), this bubble
 * opens the drawer overlay on whatever page the merchant is currently on.
 *
 * Behaviour:
 *   - Hidden on /home (the widget is also hidden there; use full ChatSurface).
 *   - Hidden while the widget is open (no double chrome).
 *   - Pulsing status dot while a turn is in-flight, so merchants who navigate
 *     away mid-turn can see progress is still happening.
 */

import type { ReactElement } from 'react'
import { useRouter } from 'next/router'
import { Button } from '@shopflo/ui'
import { Sparkle } from '@phosphor-icons/react'
import { useCopilot } from '../../lib/copilot/useCopilot'

export default function CopilotTrigger(): ReactElement | null {
  const router = useRouter()
  const widgetOpen = useCopilot(ctx => ctx.widgetOpen)
  const setWidgetOpen = useCopilot(ctx => ctx.setWidgetOpen)
  // Status-only slice — the trigger must not re-render per streamed token.
  const chatStatus = useCopilot(ctx => ctx.chat.status)

  if (router.pathname === '/home') return null
  if (widgetOpen) return null

  const inFlight = chatStatus === 'sending' || chatStatus === 'streaming'

  return (
    <Button
      variant='primary'
      size='lg'
      onClick={() => setWidgetOpen(true)}
      aria-label={inFlight ? 'Open Flo-AI (turn in progress)' : 'Open Flo-AI'}
      className='fixed bottom-6 right-6 z-10 gap-2 rounded-full pl-3 pr-3.5'
    >
      <Sparkle size={16} weight='fill' aria-hidden='true' />
      <span>Flo-AI</span>
      {inFlight ? (
        <span className='relative ml-0.5 inline-flex h-2 w-2' aria-hidden='true'>
          <span className='absolute inset-0 rounded-full bg-emerald-300 opacity-75 animate-ping' />
          <span className='relative inline-flex rounded-full h-2 w-2 bg-emerald-400' />
        </span>
      ) : null}
    </Button>
  )
}
